<script setup lang="ts">
import { useConfigStore } from '~/store/useConfigStore'
import { useMenu } from '~/composables/useMenu'
import type { Menu } from '~/types/models/menu'
import { useTickerInfo } from '~/composables/useTickerInfo'

const isSearchOpened = ref<boolean>(false)
const isMobileMenuOpened = ref<boolean>(false)
const activeMenuId = ref<number | null>(null)

const config = useConfigStore()

const { resolveMenuBySlug } = useMenu()

const headerMenuItems = computed<Menu.MenuItem[]>(() => resolveMenuBySlug('header')?.menuItems || [])

const { ticker, tickerData } = useTickerInfo()

const toggleMenu = (id: number): void => {
  activeMenuId.value = activeMenuId.value === id ? null : id
}
</script>

<template>
  <div class="header__overlay">
    <header class="header">
      <div class="header__wrapper wrapper wrapper--fluid">
        <div
          v-if="config.websiteLogo"
          class="header__logo"
        >
          <NuxtLink href="/">
            <img
              :src="config.websiteLogo.src"
              :srcset="config.websiteLogo.srcset"
              :alt="config.websiteTitle || ''"
            >
          </NuxtLink>
        </div>
        <nav
          v-if="headerMenuItems"
          :class="{ 'header__nav--opened': isMobileMenuOpened }"
          class="header__nav"
        >
          <ul class="header__nav-menu">
            <template v-for="menuItem in headerMenuItems">
              <Component
                :is="menuItem.component"
                v-if="menuItem.component && menuItem.enabled"
                :key="menuItem.id"
                :item="menuItem"
                class="header__nav-menu-item"
                :class="{ 'header__nav-menu-item--dropdown': menuItem.children.length > 0 }"
                children-wrapper-class="header__nav-submenu-body"
                :active-menu-id="activeMenuId"
                @toggle="toggleMenu"
              />
            </template>
          </ul>
        </nav>
        <div
          v-if="ticker"
          class="header__stats"
        >
          <div class="header__stats-item">
            <h3 class="header__stats-item-label">
              Stock
            </h3>
            <span class="header__stats-item-value">{{ ticker }}</span>
          </div>
          <ClientOnly>
            <div
              v-if="tickerData"
              class="header__stats-item"
            >
              <h3 class="header__stats-item-label">
                Price
              </h3>
              <span class="header__stats-item-value">${{ tickerData.lastPrice }}</span>
            </div>
          </ClientOnly>
        </div>
        <!-- <div
          :class="{ 'header__search&#45;&#45;opened': isSearchOpened }"
          class="header__search"
        >
          <button
            class="header__search-button"
            @click="isSearchOpened = !isSearchOpened"
          >
            <Icon
              v-if="!isSearchOpened"
              class="header__search-button-icon"
              icon="ion:search-outline"
              role="presentation"
            />
            <Icon
              v-else
              class="header__search-button-icon"
              icon="grommet-icons:close"
              role="presentation"
            />
            <span class="sr-only">Click to open Search</span>
          </button>
          <form
            class="header__search-form"
            action="/"
          >
            <label
              for="searchField"
              class="sr-only"
            >Search</label>
            <input
              id="searchField"
              class="header__search-field"
              name="searchField"
              type="search"
              placeholder="Search..."
            >
            <button
              class="header__search-submit"
              type="submit"
            >
              <span class="sr-only">Submit</span>
              <Icon
                icon="ion:search-outline"
                role="presentation"
              />
            </button>
          </form>
        </div>
        <div class="header__actions">
          <a
            href="https://selina.roundtable.xyz/"
            target="_blank"
            class="button button--primary"
          >Verify Shares</a>
        </div> -->
        <button
          class="header__mobile-menu-button"
          :class="{ opened: isMobileMenuOpened }"
          @click.prevent="
            isMobileMenuOpened = !isMobileMenuOpened;
            isSearchOpened = false;
          "
        >
          <span class="sr-only">Click/tap to open the mobile menu</span>
          <svg
            width="32px"
            height="32px"
            viewBox="0 0 32 32"
          >
            <g>
              <line
                x1="0"
                y1="11"
                x2="32"
                y2="11"
                stroke-width="1"
              />
              <line
                x1="0"
                y1="22"
                x2="32"
                y2="22"
                stroke-width="1"
              />
            </g>
            <g>
              <line
                x1="0"
                y1="16"
                x2="32"
                y2="16"
                stroke-width="1"
              />
              <line
                x1="0"
                y1="16"
                x2="32"
                y2="16"
                stroke-width="1"
              />
            </g>
          </svg>
        </button>
      </div>
    </header>
  </div>
</template>

<style lang="scss">
.header {
  @apply fixed top-0 left-0 z-[1001] w-full h-[70px] bg-base-background flex items-center border-b border-b-light-background;
  @apply md:h-[95px];

  &__overlay {
    @apply h-[70px];
    @apply md:h-[95px];
  }

  &__wrapper {
    @apply flex items-center h-full px-[15px];
    @apply 3xl:px-0;
  }

  &__logo {
    @apply shrink-0 mr-auto;
    @apply 2xl:mr-[50px];

    img {
      @apply w-auto h-[22px];
      @apply md:h-[30px];
    }
  }

  &__nav {
    @apply grow h-screen flex flex-col items-center fixed top-[70px] -right-full bg-base-background w-full transition-[right] duration-300 overflow-y-auto;
    @apply md:w-[370px] md:top-[95px];
    @apply 2xl:static 2xl:min-h-[unset] 2xl:w-auto 2xl:flex-row 2xl:overflow-y-visible 2xl:h-full 2xl:overflow-x-auto;

    &--opened {
      @apply right-0;
    }

    &-menu {
      @apply flex flex-col items-center w-full px-[20px] py-[70px] box-border;
      @apply 2xl:flex-row 2xl:h-full 2xl:px-0 2xl:w-auto 2xl:py-0;

      &-item {
        @apply last:mr-0 mb-[20px] text-[20px] text-dark-text h-full inline-flex items-center last:mb-[100px] whitespace-nowrap;
        @apply 2xl:mr-[30px] 2xl:mb-0 2xl:text-[16px] 2xl:last:mb-0;

        a,
        button {
          @apply underline underline-offset-4 decoration-transparent transition-colors duration-300;
          @apply hover:decoration-dark-text;
        }

        &--dropdown {
          @apply max-2xl:flex max-2xl:flex-col;

          > a,
          > button {
            @apply inline-flex items-center cursor-pointer;
            @apply after:block after:-translate-y-[4px] after:ml-[5px] after:w-[20px] after:h-[14px] after:content-chevron after:origin-bottom after:transition-transform after:duration-300;
            @apply 2xl:after:-translate-y-[5px] 2xl:after:ml-[3px] 2xl:after:w-[14px] 2xl:after:h-[10px];

            [data-theme="dark"] & {
              @apply after:content-chevron-light;
            }
          }
        }

        &--dropdown:hover .header__nav-submenu-body {
          @apply delay-0;
          @apply 2xl:block 2xl:visible;
        }

        &--opened .header__nav-submenu-body {
          @apply delay-0 block visible;
        }

        &--dropdown:hover > a,
        &--dropdown:hover > button {
          @apply 2xl:after:rotate-180 2xl:after:-translate-y-[5px];
        }

        &--opened > a,
        &--opened > button {
          @apply after:rotate-180 after:-translate-y-[8px];
          @apply 2xl:after:-translate-y-[5px];
        }
      }
    }

    &-submenu-body {
      @apply static top-full left-0 w-full bg-base-background pt-[30px] max-2xl:hidden transition-[0.2s_1.5s] text-center;
      @apply 2xl:absolute 2xl:border-y 2xl:border-y-light-background 2xl:py-[30px] 2xl:invisible 2xl:text-left;

      .wrapper > ul {
        @apply flex flex-wrap -mx-[20px];
      }
    }
  }

  &__stats {
    @apply ml-[30px] hidden items-center shrink-0;
    @apply md:flex;

    &-item {
      @apply flex items-center mr-[20px] last:mr-0;

      &-label {
        @apply text-[16px] text-dark-text opacity-80 mr-[5px];
      }

      &-value {
        @apply text-[16px] text-dark-text;
      }
    }
  }

  &__search {
    @apply ml-[20px] shrink-0 flex items-center;
    @apply md:relative;
    @apply lg:ml-[35px];

    &-button {
      &-icon {
        @apply text-[24px];
      }
    }

    &-form {
      @apply w-0 overflow-hidden absolute top-[70px] right-0 flex items-center border border-transparent transition-all duration-300;
      @apply md:top-1/2 md:right-full md:-translate-y-1/2;
    }

    &--opened &-form {
      @apply w-full border-dark-text pr-[15px] bg-base-background;
      @apply md:w-[300px] md:mr-[10px];
    }

    &-field {
      @apply grow h-[40px] px-[15px] bg-transparent;
    }

    &-submit {
      @apply text-[24px];
    }
  }

  &__actions {
    @apply ml-[20px] shrink-0 hidden;
    @apply md:block;
  }

  &__mobile-menu-button {
    @apply h-[32px] cursor-pointer z-[2] ml-[20px];
    @apply 2xl:hidden;

    svg {
      @apply stroke-dark-text transition-all duration-300;

      g {
        @apply first:opacity-100 first:transition-opacity first:delay-0 first:duration-300;
        @apply last:opacity-0 last:transition-opacity last:delay-0 last:duration-300;

        &:first-child {
          line {
            @apply transition-transform delay-200 duration-300 translate-y-0;
          }
        }

        &:last-child {
          line {
            @apply transition-transform duration-300 rotate-0 origin-center;
          }
        }
      }
    }

    &.opened {
      svg {
        g {
          &:first-child {
            @apply opacity-0;

            line {
              @apply transition-transform duration-300;
              @apply first:translate-y-[7px] last:-translate-y-[7px];
            }
          }

          &:last-child {
            @apply opacity-100;

            line {
              @apply transition-transform delay-200 duration-300;
              @apply first:rotate-45 last:-rotate-45;
            }
          }
        }
      }
    }
  }
}
</style>
